import { cn } from '~/utils/cn';

import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';

export interface MetricDelta {
  value: string;
  direction: 'up' | 'down' | 'flat';
  style?: 'pill' | 'inline';
  note?: string;
}

export interface MetricFooterItem {
  label: string;
  value: string;
  valueClassName?: string;
}

interface PropsMetricCard {
  label: string;
  value: string;
  decimals?: string;
  delta?: MetricDelta;
  footer?: MetricFooterItem[];
  onClick?: () => void;
  className?: string;
}

export default function MetricCard({
  label,
  value,
  decimals,
  delta,
  footer,
  onClick,
  className,
}: PropsMetricCard) {
  const directionColor =
    delta?.direction === 'up'
      ? 'color-warning-04'
      : delta?.direction === 'down'
        ? 'color-success-06'
        : 'color-intense-06';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full min-w-0 text-left flex-col gap-smaller rounded-medium bg-intense-01 px-small py-medium shadow-level1',
        onClick && 'hover:opacity-95',
        className,
      )}
    >
      <Text className="text-captionLG color-intense-06 uppercase">{label}</Text>
      <div className="flex items-end gap-quark min-w-0 flex-wrap">
        <Text className="text-display1 color-complement-01 break-words min-w-0">{value}</Text>
        {decimals ? (
          <Text className="text-headingH2 color-complement-01 mb-[6px]">{decimals}</Text>
        ) : null}
      </div>
      {delta ? (
        <div className="flex items-center gap-quark">
          <span
            className={cn(
              'inline-flex items-center gap-quark px-smaller py-quark rounded-full',
              delta.direction === 'up'
                ? 'bg-warning-01'
                : delta.direction === 'down'
                  ? 'bg-success-01'
                  : 'bg-intense-03',
            )}
          >
            <Icon
              name={
                delta.direction === 'up'
                  ? 'icon-arrow-up'
                  : delta.direction === 'down'
                    ? 'icon-arrow-up'
                    : 'icon-less'
              }
              size={12}
              color={
                delta.direction === 'up'
                  ? '#992929'
                  : delta.direction === 'down'
                    ? '#007831'
                    : '#606060'
              }
              className={delta.direction === 'down' ? 'rotate-180' : ''}
              rounded={false}
            />
            <Text className={cn('text-captionLG', directionColor)}>
              {delta.value}
            </Text>
          </span>
          {delta.note ? (
            <Text className="text-captionMD color-intense-06">{delta.note}</Text>
          ) : null}
        </div>
      ) : null}
      {footer && footer.length ? (
        <div className="mt-quark grid grid-cols-2 gap-smaller border-t border-intense-03 pt-smaller">
          {footer.map((f) => (
            <div key={f.label}>
              <Text className="text-captionMD color-intense-06">{f.label}</Text>
              <Text className={cn('text-bodyLG color-complement-01', f.valueClassName)}>
                {f.value}
              </Text>
            </div>
          ))}
        </div>
      ) : null}
    </button>
  );
}
